import type { CapabilityWritePath } from "../telemetry";

const OUTLOOK_CAPABILITY_BY_HANDLER: Readonly<Record<string, string>> = {
  "compose.insert": "DR-01",
  "compose.open": "DR-02",
  "attachment.open_draft": "DR-01",
  "calendar.open": "CM-01",
  "calendar.open_teams": "CM-01",
};

const WORD_CAPABILITY_BY_HANDLER: Readonly<Record<string, string>> = {
  "word.replace_selection": "DE-03",
  "word.insert_at_cursor": "DE-03",
  "word.append_to_end": "DE-03",
  "ui.pill.word.replace_selection": "DE-03",
  "ui.pill.word.insert_at_cursor": "DE-03",
  "ui.pill.word.append_to_end": "DE-03",
  "word.apply_styles": "FS-01",
  "ui.pill.word.apply_styles": "FS-01",
  "word.strip_styles": "FS-02",
  "word.strip_selection_styles": "FS-02",
  "ui.pill.word.strip_styles": "FS-02",
  "word.insert_toc": "FS-03",
  "ui.pill.word.insert_toc": "FS-03",
  "word.apply_review": "RV-01",
  "word.apply_review_edits": "RV-01",
  "word.insert_comments": "RV-01",
  "ui.pill.word.apply_review": "RV-01",
  "ui.pill.word.apply_review_edits": "RV-01",
  "word.list_comments": "RV-02",
  "word.resolve_comment": "RV-02",
  "word.delete_comment": "RV-02",
  "word.insert_comment": "RV-02",
  "ui.pill.word.insert_comment": "RV-02",
  "ui.pill.word.choose_comment_occurrence": "RV-02",
  "word.accept_changes": "RV-04",
  "word.reject_changes": "RV-04",
  "ui.pill.word.review_changes": "RV-04",
  "word.insert_template": "TO-01",
  "ui.pill.word.insert_template": "TO-01",
  "word.insert_outline": "TO-02",
  "ui.pill.word.insert_outline": "TO-02",
  "word.replace_all": "DP-01",
  "ui.pill.word.replace_all": "DP-01",
  "word.insert_bates": "DP-02",
  "word.remove_bates": "DP-02",
  "ui.pill.word.bates": "DP-02",
  "ui.pill.word.remove_bates": "DP-02",
  "word.insert_page_numbers": "DP-03",
  "word.remove_page_numbers": "DP-03",
  "ui.pill.word.page_numbers": "DP-03",
  // AIVA-7868 — removal is the same DP-03 capability as insertion, reached by
  // its own pill because the write handler had no route to it.
  "ui.pill.word.remove_page_numbers": "DP-03",
  "word.insert_hyperlink": "DP-04",
  "ui.pill.word.insert_hyperlink": "DP-04",
  "word.insert_email": "DP-05",
  "ui.pill.word.insert_email": "DP-05",
};

export const NON_CAPABILITY_HANDLERS: ReadonlySet<string> = new Set([
  "mailbox.read_current",
  "ui.render_email_cards",
  "word.get_selection_context",
  "word.get_document_structure",
  // AIVA-8606. /format's whole-document read; the write is word.apply_styles (FS-01).
  "word.get_format_candidates",
  "word.read_paragraphs",
  "word.get_review_items",
  "word.list_comments",
  // AIVA-8147. A read, like every other entry here: it reports whether the
  // document carries page numbers and writes nothing. Deliberately NOT DP-03 —
  // that counts uses of the page-numbering capability, and a user asking
  // whether their document is numbered has not used it.
  "word.check_page_numbers",
  "word.set_tracking",
  "word.highlight_text",
  "word.clear_highlights",
  "ui.pill.word.clear_highlights",
  "word.insert_table",
  "ui.pill.word.insert_table",
  "clipboard.copy",
  "word.copy_text",
  "ui.pill.word.copy_text",
  "ui.pill.office.clipboard_copy",
]);

const NON_CAPABILITY_PREFIXES: readonly string[] = ["word.docreview."];

export function isNonCapabilityHandler(handler: string): boolean {
  if (NON_CAPABILITY_HANDLERS.has(handler)) return true;
  return NON_CAPABILITY_PREFIXES.some((prefix) => handler.startsWith(prefix));
}

export function outlookCapabilityFor(
  handler: string,
  args?: Record<string, unknown>,
): string | undefined {
  if (handler === "compose.reply") {
    return args?.mode === "reply_all" ? "DR-03" : "DR-01";
  }
  return OUTLOOK_CAPABILITY_BY_HANDLER[handler];
}

export function wordCapabilityFor(handler: string): string | undefined {
  return WORD_CAPABILITY_BY_HANDLER[handler];
}

export function capabilityForHandler(
  channel: string | null,
  handler: string,
  args?: Record<string, unknown>,
): string | undefined {
  if (channel === "word") return wordCapabilityFor(handler);
  if (channel === "outlook") return outlookCapabilityFor(handler, args);
  return undefined;
}

export function writePathFor(
  result: Record<string, unknown> | undefined,
  args?: Record<string, unknown>,
): CapabilityWritePath | undefined {
  const source = typeof result?.source === "string" ? result.source : undefined;
  switch (source) {
    case "compose_fast":
      return "compose_fast";
    case "native_form":
      return "native_form";
    case "graph_event":
      return "graph_event";
    case "graph_draft":
      return "graph_new";
    case "graph_create_reply":
      return args?.mode === "reply_all" ? "graph_replyall" : "graph_reply";
    default:
      return undefined;
  }
}

export function graphUsedFor(result: Record<string, unknown> | undefined): boolean | undefined {
  const path = writePathFor(result);
  if (path === undefined) return undefined;
  return path === "graph_new" || path === "graph_event" || path === "graph_reply" || path === "graph_replyall";
}

export function appliedCountFor(result: Record<string, unknown> | undefined): number | undefined {
  if (!result) return undefined;
  for (const key of ["appliedCount", "applied", "count", "replaced", "changed"]) {
    const value = result[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return undefined;
}

export function sectionCountFor(
  result: Record<string, unknown> | undefined,
  args?: Record<string, unknown>,
): number | undefined {
  const reported = result?.sections;
  if (typeof reported === "number" && Number.isFinite(reported)) return reported;
  if (Array.isArray(args?.sections)) return args.sections.length;
  return undefined;
}

const UNSUPPORTED_REASONS: ReadonlySet<string> = new Set([
  "unsupported",
  "unsupported_host",
  "offset_unsupported",
]);

export function isUnsupportedApiResult(result: Record<string, unknown> | undefined): boolean {
  const reason = result?.reason;
  return typeof reason === "string" && UNSUPPORTED_REASONS.has(reason);
}
